/**
 * A pending draft's persist (`client.rows.pending(type)` → `promote`): the
 * state a draft row holds between its first buffered update and the server's
 * answer to its create POST — how many updates it buffered, which of them set
 * a field the POST cannot carry (`icon`, `heightPx`), the updates still
 * waiting on the persist, and where the row lives once it landed — and the
 * verbs that settle them (`submit`, `promote`).
 *
 * **SDK-internal**: built by `SheetRow.Handle` for its own row, which hands it
 * the row through `PendingRowPersist.Row` (its ledger entry and transport,
 * its liveness, the client's disposal and the failed-write recovery). It
 * names no model type, so it sits outside the rows' type knot.
 *
 * @internal SDK-private; `lib.ts` does not export it.
 */

import type { Option } from "@tento-lona/sheets";
import { RowNetworkKey, type RowLocalId, RowUuid, type SheetId, SheetNetworkKey } from "@tento-lona/sheets";
import { RowWrites } from "@tento-lona/sheets/internal-sdk";
import type { Rational } from "@tento-core/rational";
import { Tracing } from "@tento-core/tracing";
import type { TypedApi } from "@tento-core/typed-api";
import { RowWriteError } from "../errors.ts";
import type { Actor } from "../invalidation.ts";
import { Network } from "../network.ts";

/**
 * Where a promoted pending row lives, how many of its buffered updates the
 * promote's POST carried, and the checked follow-up PATCH of the fields the
 * POST cannot carry (`icon`, `heightPx`) — resolved at once when none of
 * those updates set one (`SheetRow.Handle.promote`).
 */
type PendingPersist = {
  readonly rowKey: string;
  readonly carried: number;
  readonly followUp: Promise<void>;
};

/**
 * One pending row update in flight, split by who answers for each part:
 * the fields the create POST carries (`label`, `timeScale`, `attributes`,
 * `formula`) and the ones only the follow-up PATCH can send (`icon`,
 * `heightPx`). Each part is its own write in the row-field ledger — entered
 * back to back, so they keep the update's place in issue order, and
 * disjoint, so settling one never touches the other's fields. `null` for a
 * part the update does not set.
 */
interface PendingRowPatchWrite {
  readonly carried: Option<RowWrites.Write>;
  readonly uncarried: Option<RowWrites.Write>;
}

/** See the module note: a pending draft's persist, over its row (`PendingRowPersist.Row`). */
export class PendingRowPersist {
  readonly #row: PendingRowPersist.Row;

  /**
   * A pending row's persist (`promote`), as the buffered updates wait on
   * it: `null` until an update of the still-pending row waits, or until
   * `promote` succeeded (`persistedAs` then answers at once).
   */
  #persistWaiters: Option<{
    readonly promise: Promise<PendingPersist>;
    readonly resolve: (persisted: PendingPersist) => void;
    readonly reject: (err: unknown) => void;
    /** Stops watching the client's disposal (see `whenPersisted`). */
    readonly unwatch: () => void;
  }> = null;
  /** Set once `promote` succeeded: where the row now lives, and how many
   *  buffered updates its POST carried. */
  #persistedAs: Option<PendingPersist> = null;
  /** Updates buffered on this pending row so far (their issue order). */
  #bufferedUpdates = 0;
  /**
   * Row-identity stage 2 (`Row.clientRowIds` on): the id this draft's
   * promote sends (a bare UUID, which the server takes as the row's id),
   * minted at its first promote and kept, so a
   * retried promote whose first POST went unanswered sends the same id and
   * the server replays the row it stored instead of creating a second one.
   * A `row_id_conflict` refusal (another owner holds it) drops it: the next
   * promote mints again. The server's answer stays authoritative: the draft
   * takes the id the POST answers (`adopt`), so a server that ignores the
   * sent id still re-keys the draft to its own.
   */
  #clientRowId: Option<RowUuid> = null;
  /**
   * Per buffered update of this pending row still standing (its issue
   * number), the fields it set that the promote's POST cannot carry
   * (`icon`, `heightPx`): `promote` sends their union in a checked
   * follow-up PATCH of the created row, which those updates wait on. An
   * update a refused promote took back leaves it (`settlePending`), so a
   * retried promote never sends a field no standing update set.
   */
  readonly #uncarriedByUpdate = new Map<number, ReadonlySet<"icon" | "heightPx">>();
  /**
   * The issue numbers of the pending updates still waiting on their
   * verdict that set an uncarried field (`settlePending`). Those the
   * promote's POST carried (issued before it was sent) await its
   * follow-up PATCH and reject with its refusal; one issued later sends
   * its own PATCH and never hears the follow-up.
   */
  readonly #uncarriedClaimants = new Set<number>();

  constructor(row: PendingRowPersist.Row) {
    this.#row = row;
  }

  /**
   * An update of the still-pending row (`update` / `updateDurable`): entered
   * in the row-field ledger (shown and announced as `actor` at once),
   * buffered, and settled on its verdict (`settlePending`) — its own PATCH
   * of the created row going out through the `mode` transport when it was
   * buffered after the promote's POST was sent. Answers that settle.
   */
  submit(change: RowWrites.Change, actor: Actor, mode: "checked" | "durable"): Promise<void> {
    const write = this.#beginPending(change, actor);
    const buffered = this.#bufferPending(change);
    return this.#settlePending(
      write,
      buffered,
      (rowKey) => this.#sendPending(write, this.#row.transport(mode, RowNetworkKey.fromWire(rowKey))),
    );
  }

  /**
   * Enter an update of this pending row in the ledger, split by who
   * answers for each part (`PendingRowPatchWrite`): the fields the create
   * POST carries, and the ones only its follow-up PATCH can send (`icon`,
   * `heightPx`). Shown and announced as `actor` at once.
   */
  #beginPending(change: RowWrites.Change, actor: Actor): PendingRowPatchWrite {
    const { icon, heightPx, ...carriedFields } = change.set;
    const uncarriedFields: RowWrites.Fields = {
      ...(icon !== undefined ? { icon } : {}),
      ...(heightPx !== undefined ? { heightPx } : {}),
    };
    const carried: RowWrites.Change = { set: carriedFields, attributes: change.attributes };
    const carriedWrite = RowWrites.fieldsOf(carried).size === 0 ? null : this.#row.begin(carried, actor);
    try {
      return {
        carried: carriedWrite,
        uncarried: Object.keys(uncarriedFields).length === 0 ? null : this.#row.begin(RowWrites.set(uncarriedFields), actor),
      };
    } catch (err) {
      // The second part failed to open: the first has no waiter to settle it.
      if (carriedWrite != null) {
        try { this.#row.ledger().refuse(carriedWrite, err, "row.update"); }
        catch (_) { /* Preserve the initiating error if a correction observer also throws. */ }
      }
      throw err;
    }
  }

  /** Send both parts of a pending update as ONE PATCH through `transport` (`RowWrites.sendAll`). */
  async #sendPending(write: PendingRowPatchWrite, transport: RowWrites.Transport): Promise<void> {
    const parts = [write.carried, write.uncarried].filter((part): part is RowWrites.Write => part != null);
    await this.#row.ledger().sendAll(parts, transport);
  }

  /** Settle the parts of a pending update: `true` accepted, `false` refused
   *  with `err`, `null` left in flight. Returns the refusal's error. */
  #settlePendingParts(
    write: PendingRowPatchWrite,
    carried: Option<boolean>,
    uncarried: Option<boolean>,
    err: unknown,
  ): RowWriteError {
    const ledger = this.#row.ledger();
    for (const [part, verdict] of [[write.carried, carried], [write.uncarried, uncarried]] as const) {
      if (part == null || verdict == null) continue;
      if (verdict) ledger.accept(part);
      else ledger.refuse(part, err, "");
    }
    return RowWrites.wrap(err, "");
  }

  /** Buffer an update of this pending row (already entered in the ledger
   *  and shown); returns its issue number. */
  #bufferPending(change: RowWrites.Change): number {
    const issued = ++this.#bufferedUpdates;
    const uncarried = new Set<"icon" | "heightPx">();
    if (change.set.icon !== undefined) uncarried.add("icon");
    if (change.set.heightPx !== undefined) uncarried.add("heightPx");
    if (uncarried.size > 0) this.#uncarriedByUpdate.set(issued, uncarried);
    return issued;
  }

  /**
   * Settle pending update number `buffered` (`write`, entered in the
   * row-field ledger before it was applied) on its verdict:
   * - the row's persist (`promote`) was refused, or the client was
   *   disposed while the row waited — the update is taken back like a
   *   refused PATCH (its fields go back to the row's confirmed state with
   *   every other update still standing replayed in issue order, announced
   *   as `SheetRow.Handle.ACTOR`) and leaves the row's buffer, so the next
   *   promote's POST never carries it; the update rejects with the
   *   promote's error. A rejection means the change is gone;
   * - the POST was sent after this update was buffered — it carried the
   *   fields it can (`label`, `timeScale`, `attributes`, `formula`), and
   *   an `icon` / `heightPx` rides the promote's checked follow-up PATCH,
   *   whose verdict this update waits on;
   * - the update was buffered after the POST was sent — `send(rowKey)`,
   *   its own PATCH of the created row.
   * A refused PATCH (follow-up or own) takes back this update alone,
   * exactly as a persisted row's `update` does: its fields go back to the
   * confirmed state (what the POST created, with the updates that landed)
   * with every surviving update replayed in issue order.
   *
   * Each portion settles on the answer that decided it:
   * when the POST carried the update, its POST-carried fields become
   * confirmed state as soon as the POST is accepted, before the follow-up
   * PATCH answers — so a refused follow-up takes back only `icon` /
   * `heightPx`, never a `label` the server already holds.
   */
  async #settlePending(
    write: PendingRowPatchWrite,
    buffered: number,
    send: (rowKey: string) => Promise<void>,
  ): Promise<void> {
    const claims = write.uncarried != null;
    if (claims) this.#uncarriedClaimants.add(buffered);
    try {
      await this.#settlePendingOn(write, buffered, send);
    } finally {
      if (claims) this.#uncarriedClaimants.delete(buffered);
    }
  }

  /** `settlePending`'s verdicts (see there). */
  async #settlePendingOn(
    write: PendingRowPatchWrite,
    buffered: number,
    send: (rowKey: string) => Promise<void>,
  ): Promise<void> {
    let persisted: PendingPersist;
    try {
      persisted = await this.#whenPersisted();
    } catch (err) {
      // Nothing reached the server: take this update back and drop it
      // from what a retried promote would carry.
      this.#uncarriedByUpdate.delete(buffered);
      this.#settlePendingParts(write, false, false, err);
      // after-failed-write: this update was never sent — the failed promote it waited on is the one that may have stored.
      throw err;
    }
    if (buffered > persisted.carried) {
      // Sent whole as its own PATCH: one answer decides both portions.
      try {
        await send(persisted.rowKey);
      } catch (err) {
        const error = this.#settlePendingParts(write, false, false, err);
        this.#row.afterFailedWrite(error, "row.update", "row.update", () =>
          this.#row.reread(RowNetworkKey.fromWire(persisted.rowKey)));
        throw error;
      }
      this.#settlePendingParts(write, true, true, null);
      return;
    }
    // The POST carried (and the server accepted) the carried portion.
    this.#settlePendingParts(write, true, null, null);
    if (write.uncarried == null) return;
    try {
      await persisted.followUp;
    } catch (err) {
      this.#settlePendingParts(write, null, false, err);
      // after-failed-write: the follow-up PATCH's one owner recovers it — `#answerFollowUp` re-reads the row.
      throw err;
    }
    this.#settlePendingParts(write, null, true, null);
  }

  /**
   * Resolves once `promote` succeeded; rejects with its failure — or with
   * `LonaSdk.Client.DisposedError` once the client is disposed while the
   * row still waits (no promote can run then), so no update of a pending
   * row waits forever.
   */
  #whenPersisted(): Promise<PendingPersist> {
    if (this.#persistedAs != null) return Promise.resolve(this.#persistedAs);
    if (this.#persistWaiters == null) {
      // `Promise.withResolvers` is ES2024 and the perf lab compiles against
      // ES2023: the executor runs synchronously, so the no-op placeholders
      // are replaced before anything can call them.
      const settle = {
        resolve: (_persisted: PendingPersist): void => {},
        reject: (_err: unknown): void => {},
      };
      const promise = new Promise<PendingPersist>((resolve, reject) => {
        settle.resolve = resolve;
        settle.reject = reject;
      });
      const unwatch = this.#row.watchDisposal((err) => this.#failPersistWaiters(err));
      this.#persistWaiters = { promise, resolve: settle.resolve, reject: settle.reject, unwatch };
    }
    return this.#persistWaiters.promise;
  }

  /** Reject every update waiting on the row's persist with `err`; a later
   *  promote starts over. */
  #failPersistWaiters(err: unknown): void {
    const waiters = this.#persistWaiters;
    this.#persistWaiters = null;
    if (waiters == null) return;
    waiters.unwatch();
    waiters.reject(err);
  }

  /**
   * Promote a pending row to a server-backed row (`SheetRow.Pending.promote`,
   * the one door). Posts the accumulated state to the server and returns
   * the created row response. The updates
   * buffered before the POST was sent resolve with it; one buffered while
   * it is in flight is sent to the created row afterwards (see `update`).
   * Throws `RowWriteError("add")` when the server refuses or does not
   * answer — and the buffered updates waiting on it are taken back and
   * reject with it, so a retried promote carries none of them. Unanswered
   * (`SdkRequestError.mayHaveStored`), the row may have been created:
   * every open handle of sheet `sheetId` re-reads its row list and lists
   * a top-level row of the draft's type (and lookup key) the server holds
   * that it does not (`RowMirror`'s `mayHaveCreated`). A row that
   * is not pending (`isPending` false: already server-backed) has nothing
   * to promote: `RowWriteError("add")` with outcome `"unaddressable"`,
   * nothing sent. `actor` stamps the `RowDataChanged` (field `"pending"`)
   * a landed promote emits — the row is no longer pending. `adopt` makes
   * this row the created one (`adoptCreatedRow`) as soon as the POST is
   * accepted, before any update waiting on the persist hears it.
   */
  async promote(
    sheetId: SheetId,
    position: Rational,
    actor: Actor,
    adopt: (created: Network.CreateRowResponse) => void,
  ): Promise<Network.CreateRowResponse> {
    try {
      this.#row.assertLive("row.promote");
    } catch (err) {
      // The updates waiting on this persist hear it too: none can land.
      this.#failPersistWaiters(err);
      throw err;
    }
    if (!this.#row.isPending()) {
      // Never sent: a server-backed row has no pending state to post.
      throw new RowWriteError({
        step: "add",
        method: "POST",
        path: "/api/sheets/{sheetKey}/row",
        failure: { outcome: "unaddressable" },
        details: `row.promote: row ${this.#row.id()} is not pending (already server-backed)`,
      });
    }
    const ledger = this.#row.ledger();
    const carried = this.#bufferedUpdates;
    // What the POST cannot carry, as the row holds it when the POST is
    // sent: the follow-up PATCH's body — the fields the standing buffered
    // updates set.
    const raw = this.#row.raw();
    const buffer = new Set<"icon" | "heightPx">();
    for (const fields of this.#uncarriedByUpdate.values()) {
      for (const field of fields) buffer.add(field);
    }
    const uncarried: Option<RowWrites.Fields> = buffer.size === 0 ? null : {
      ...(buffer.has("icon") ? { icon: raw.icon ?? null } : {}),
      ...(buffer.has("heightPx") ? { heightPx: raw.heightPx ?? null } : {}),
    };
    // The follow-up enters the ledger in its issue place — after every
    // update the POST carries, before any buffered while the POST is in
    // flight — so its refusal takes back its own contribution alone, never
    // a newer write of the same fields, accepted or still pending,
    // whatever value it set.
    const followUpWrite = uncarried == null ? null : this.#row.begin(RowWrites.set(uncarried), this.#row.correction);
    let created: Network.CreateRowResponse;
    try {
      created = await this.#postPending(sheetId, position, this.#mintRowId());
    } catch (err) {
      if (err instanceof RowWriteError && err.reason === "row_id_conflict") this.#clientRowId = null;
      // Nothing was sent that holds: the follow-up never goes out, so its
      // entry leaves the ledger, and every update waiting now — buffered
      // before the POST or while it was in flight — hears the refusal and
      // is taken back (`settlePending`). A later promote starts over from
      // the row as the standing updates leave it.
      if (followUpWrite != null) ledger.refuse(followUpWrite, err, "");
      this.#failPersistWaiters(err);
      // No verdict (or one the SDK could not read): the row may have been
      // created in `sheetId` — every open handle of that sheet re-reads its
      // row list and lists it. The draft stays pending. With the switch off
      // the POST carries no key the server could answer a retry by, so a
      // retried promote may still create a second row (CHANGELOG Known
      // gaps); with it on, the retry sends the same minted id, which the
      // server answers with the row it stored.
      this.#row.afterFailedWrite(err, "row.promote", "row.promote", () => {
        this.#row.mayHaveCreated({
          sheet: sheetId,
          type: raw.type,
          lookupKey: raw.lookupKey == null ? null : RowNetworkKey.parse(raw.lookupKey),
        });
        return undefined;
      });
      throw err;
    }
    const rowKey = created.id.toString();
    adopt(created);
    let followUp: Promise<void> = Promise.resolve();
    if (uncarried != null && followUpWrite != null) {
      // The POST created the row without them: the server's state the
      // follow-up (and every update in flight) lays over.
      ledger.confirm(this.#row.id(), RowWrites.restrict({}, RowWrites.fieldsOf(RowWrites.set(uncarried))));
      followUp = ledger.send(followUpWrite, this.#row.transport("checked", RowNetworkKey.fromWire(rowKey))).then(
        () => {},
        (err: unknown) => {
          throw RowWrites.wrap(err, "");
        },
      );
      // The updates the POST carried that set an uncarried field await
      // this PATCH and reject with its refusal; one buffered after the
      // POST was sent sends its own PATCH and is no claimant.
      const claimed = [...this.#uncarriedClaimants].some((issued) => issued <= carried);
      this.#answerFollowUp(followUp, followUpWrite, uncarried, rowKey, claimed);
    }
    this.#uncarriedByUpdate.clear();
    const persisted = { rowKey, carried, followUp };
    this.#persistedAs = persisted;
    const waiters = this.#persistWaiters;
    this.#persistWaiters = null;
    waiters?.unwatch();
    waiters?.resolve(persisted);
    this.#row.promoted(actor);
    return created;
  }

  /**
   * Settle the promote's follow-up PATCH (`write`, its entry in the
   * row-field ledger) on its answer — every follow-up, so its refusal
   * always has an owner. Accepted: it lands like any update. Refused: its
   * contribution alone is taken back — the fields it sent go back to what
   * the POST created (none) with every other update of them still
   * standing replayed in issue order, so a newer write of the same field,
   * accepted or still pending, keeps showing even when it set the same
   * value — announced as `SheetRow.Handle.ACTOR`. When
   * no claimant awaited it (`claimed` false — defensive: a refused
   * promote takes back and drops the updates that set its fields, so a
   * follow-up always has one) the refusal is logged here, never left
   * unhandled; otherwise the claimants reject with it. The promote itself
   * stays resolved: the row exists.
   */
  #answerFollowUp(
    followUp: Promise<void>,
    write: RowWrites.Write,
    sent: RowWrites.Fields,
    rowKey: string,
    claimed: boolean,
  ): void {
    const ledger = this.#row.ledger();
    followUp.then(
      () => {
        ledger.accept(write);
      },
      (err: unknown) => {
        const error = ledger.refuse(write, err, "");
        this.#row.afterFailedWrite(error, "row.update", "row.promote", () =>
          this.#row.reread(RowNetworkKey.fromWire(rowKey)));
        if (claimed) return;
        Tracing.warn("net")("sheet-row/promote-follow-up-refused", {
          rowKey,
          fields: Object.keys(sent).join(","),
          error: err instanceof Error ? err.message : String(err),
        });
      },
    );
  }

  /** The id this promote sends (`#clientRowId`), minted once; `null` while the switch is off. */
  #mintRowId(): Option<RowUuid> {
    if (!this.#row.clientRowIds()) return null;
    return this.#clientRowId ??= RowUuid.make();
  }

  /** The promote's POST of the accumulated state, with the client-minted `id` when there is one. */
  #postPending(
    sheetId: SheetId,
    position: Rational,
    id: Option<RowUuid>,
  ): Promise<Network.CreateRowResponse> {
    const raw = this.#row.raw();
    const body = Network.NewRowBody.make({
      type: raw.type,
      position,
      label: raw.label ?? null,
      timeScale: raw.timeScale !== "month" ? raw.timeScale : null,
      attributes: raw.attributes ?? null,
      formula: raw.formula ?? null,
      lookupKey: raw.lookupKey ?? null,
      surface: null,
      // The client-minted id, `null` while the switch is off (the server reads `null` as no id).
      id,
    });
    // after-failed-write: its one caller, `promote`, recovers — every open handle of the sheet re-reads its rows.
    return RowWriteError.check(
      "add",
      "POST",
      "/api/sheets/{sheetKey}/row",
      () =>
        this.#row.api().postResult("/api/sheets/{sheetKey}/row", {
          path: { sheetKey: SheetNetworkKey.of(sheetId) },
          body,
        }),
    );
  }
}

/** Types that go with `PendingRowPersist`: `Row`, `MayHaveCreated`. */
export namespace PendingRowPersist {
  /**
   * The draft row a persist settles, as its `SheetRow.Handle` hands it over:
   * the row's identity and wire state, its writes in the client's row-field
   * ledger, its liveness, and the client-side pieces a refusal reaches
   * (the client's disposal, the failed-write recovery, the rename/promote
   * mirror, the row's announcement).
   */
  export interface Row {
    /** The SDK's own correction actor (`SheetRow.Handle.ACTOR`): the follow-up PATCH is entered as it. */
    readonly correction: Actor;
    /** The row's id (re-keyed in place by the promote: the same `RowLocalId`). */
    id(): RowLocalId;
    /** The row's wire state, as the POST sends it. */
    raw(): Network.SheetRowDb;
    /** The client's row-field ledger. */
    ledger(): RowWrites;
    /** Enter `change` as a write of the row, shown and announced as `actor` at once. */
    begin(change: RowWrites.Change, actor: Actor): RowWrites.Write;
    /** The row's transport: checked, or the client's durable executor, addressed by `rowKey`. */
    transport(mode: "checked" | "durable", rowKey: RowNetworkKey): RowWrites.Transport;
    /** The client's API the POST goes through. */
    api(): TypedApi<Network.Routes>;
    /** Throw once the row's owners are gone (`verb` names the attempt). */
    assertLive(verb: string): void;
    /** Whether the row is still pending (its key is a pending one). */
    isPending(): boolean;
    /** Whether a promote sends a client-minted row id (row-identity stage 2; the client's switch, off by default). */
    clientRowIds(): boolean;
    /** Call `onDisposed` with the client's `DisposedError` once the client is disposed; answers the unwatch. */
    watchDisposal(onDisposed: (err: Error) => void): () => void;
    /** A failed write's recovery (`afterFailedWrite`), owned by the row's `ownerVerb` cell owner. */
    afterFailedWrite(err: unknown, ownerVerb: string, verb: string, recover: () => Promise<void> | undefined): void;
    /** Re-read the row (a failed PATCH's recovery), its view shown as the SDK's correction. */
    reread(rowKey: RowNetworkKey): Promise<void>;
    /** An unanswered promote may have created the row: every open handle of `sheet` re-reads its rows. */
    mayHaveCreated(created: MayHaveCreated): void;
    /** The promote landed: announce the row is no longer pending, as `actor`. */
    promoted(actor: Actor): void;
  }

  /** A row an unanswered promote into `sheet` may have created: its wire `type` and lookup key. */
  export type MayHaveCreated = {
    readonly sheet: SheetId;
    readonly type: string;
    readonly lookupKey: Option<RowNetworkKey>;
  };
}
